import _ from 'underscore';
import Backbone from "backbone";
import { NonRealTimeVAD } from "@ricky0123/vad-web";
import AudioBufferUtils from "models/audioEditor/AudioBufferUtils";




const SILERO_VAD_TARGET_SAMPLE_RATE = 16000;

// Silero VAD Configuration Presets
const SILERO_CONFIGS = {
    SILENCE_DETECTION: {
        // Aggressive silence detection - optimized for finding pure silence
        minSpeechMs: 100,        // Ignore very short sounds
        maxSpeechMs: 20000,      // Maximum speech segment length
        preSpeechPadMs: 0,       // No padding for exact silence boundaries
        postspeechPadMs: 0,      // No padding for exact silence boundaries
        speechPadMs: 0,          // No general padding
        
        negativeSpeechThreshold: 0.4,  // Aggressive silence detection
        positiveSpeechThreshold: 0.9,  // Very confident speech detection
        
        redemptionFrames: 1,     // Minimal frames for fast response
        frameSamples: 512        // Standard granularity
    },
    
    WORD_BOUNDARY: {
        // Sensitive detection around word boundaries - catches unclear speech
        minSpeechMs: 30,         // Catch very short utterances
        maxSpeechMs: 20000,      // Maximum speech segment length
        preSpeechPadMs: 80,      // Padding to protect word beginnings
        postspeechPadMs: 80,     // Padding to protect word endings
        speechPadMs: 80,         // General speech padding
        
        negativeSpeechThreshold: 0.2,  // Conservative silence detection
        positiveSpeechThreshold: 0.6,  // Sensitive to unclear speech
        
        redemptionFrames: 3,     // More frames for better accuracy
        frameSamples: 256        // Fine-grained detection
    },
    
    BALANCED: {
        // Current default configuration - balanced approach
        minSpeechMs: 50,         // Minimum speech segment length
        maxSpeechMs: 20000,      // Maximum speech segment length
        preSpeechPadMs: 100,     // Padding before speech starts
        postspeechPadMs: 100,    // Padding after speech ends
        speechPadMs: 100,        // General speech padding
        
        negativeSpeechThreshold: 0.15,  // Lower = more sensitive to silence
        positiveSpeechThreshold: 0.85,  // Higher = more confident speech detection
        
        redemptionFrames: 2,     // Fewer frames for faster response
        frameSamples: 512        // Smaller frame size for more granular detection
    }
};

const VoiceActivityDetectionHelper = Backbone.Model.extend({
    idAttribute: "id",

    constructor: function(attributes, options) {
        Backbone.Model.apply(this, [attributes, options]);
        this._voiceActivityDetectorInstance =  null;
        this._voiceActivityDetectorInstanceBeingCreated =  null;
        
        // Cache for different detector configurations
        this._voiceActivityDetectorInstances = {};
        this._voiceActivityDetectorInstancesBeingCreated = {};
        
        // Track initialization failures to avoid repeated attempts
        this._initializationFailures = {};
    },  

    _createVoiceActivityDetector : async function(){
        // Use balanced configuration for default detector
        return await this._createVoiceActivityDetectorWithConfig(SILERO_CONFIGS.BALANCED);
    },
    
    _createVoiceActivityDetectorWithConfig : async function(config){
        try {
            console.log('[VAD] Creating Silero VAD detector with config (v0.0.24):', config);
            
            // The newer version of @ricky0123/vad-web handles model loading better
            // and doesn't require manual path specification in most cases
            
            // Add timeout to prevent hanging
            const vadPromise = NonRealTimeVAD.new(config);
            
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('VAD initialization timeout after 30 seconds')), 30000);
            });
            
            const voiceActivityDetector = await Promise.race([vadPromise, timeoutPromise]);
            console.log('[VAD] Silero VAD detector created successfully with v0.0.24');
            return voiceActivityDetector;
        } catch (error) {
            console.error('[VAD] Failed to create Silero VAD detector with v0.0.24:', error);
            throw new Error(`Silero VAD initialization failed: ${error.message}`);
        }
    },
    
    _checkVadFilesAvailable: async function() {
        // v0.0.24 includes both legacy and v5 models
        const requiredFiles = [
            '/silero_vad_legacy.onnx',
            '/silero_vad_v5.onnx', 
            '/vad.worklet.bundle.min.js'
        ];
        
        for (const file of requiredFiles) {
            try {
                const response = await fetch(file, { method: 'HEAD' });
                if (!response.ok) {
                    console.warn(`[VAD] Optional file not found: ${file}`);
                }
            } catch (error) {
                console.warn(`[VAD] Could not verify file ${file}:`, error.message);
            }
        }
        console.log('[VAD] VAD file check completed for v0.0.24');
    },

    getVoiceActivityDetector : async function(){
        if(this._voiceActivityDetectorInstance){
            return this._voiceActivityDetectorInstance;
        }
        if(this._voiceActivityDetectorInstanceBeingCreated){
            return await this._voiceActivityDetectorInstanceBeingCreated;
        }else{
            this._voiceActivityDetectorInstanceBeingCreated = this._createVoiceActivityDetector();
            this._voiceActivityDetectorInstance = await this._voiceActivityDetectorInstanceBeingCreated;
            this._voiceActivityDetectorInstanceBeingCreated = null;
            return this._voiceActivityDetectorInstance;
        }
    },
    
    getVoiceActivityDetectorWithConfig : async function(configType = 'BALANCED'){
        const configKey = configType.toUpperCase();
        
        // Check if we've already failed to initialize this config
        if (this._initializationFailures[configKey]) {
            throw new Error(`VAD initialization previously failed for config ${configKey}: ${this._initializationFailures[configKey]}`);
        }
        
        if(this._voiceActivityDetectorInstances[configKey]){
            return this._voiceActivityDetectorInstances[configKey];
        }
        
        if(this._voiceActivityDetectorInstancesBeingCreated[configKey]){
            return await this._voiceActivityDetectorInstancesBeingCreated[configKey];
        }
        
        try {
            const config = SILERO_CONFIGS[configKey] || SILERO_CONFIGS.BALANCED;
            this._voiceActivityDetectorInstancesBeingCreated[configKey] = this._createVoiceActivityDetectorWithConfig(config);
            this._voiceActivityDetectorInstances[configKey] = await this._voiceActivityDetectorInstancesBeingCreated[configKey];
            this._voiceActivityDetectorInstancesBeingCreated[configKey] = null;
            
            return this._voiceActivityDetectorInstances[configKey];
        } catch (error) {
            console.error(`[VAD] Failed to get VAD detector with config ${configKey}:`, error);
            
            // Clear the failed attempt and record the failure
            this._voiceActivityDetectorInstancesBeingCreated[configKey] = null;
            this._initializationFailures[configKey] = error.message;
            
            throw error;
        }
    },

    containsVoiceActivity : function(voiceActivityDetectionResultArray, startTimeInMilliseconds, endTimeInMilliseconds){
        if(!voiceActivityDetectionResultArray){
            // No VAD data available for this region — caller should treat
            // "unknown" as "possibly speech" (fail-safe). We return false here
            // to preserve the original contract; the fail-safe policy lives in
            // the caller (see SequencerNode.containsVoiceActivity).
            return false;
        }
        for(let i =0; i < voiceActivityDetectionResultArray.length; i++){
            const startAndEndPeriod = voiceActivityDetectionResultArray[i];
            const startPeriod = startAndEndPeriod[0];
            const endPeriod = startAndEndPeriod[1];
            // Two intervals [a, b] and [c, d] overlap iff a <= d && c <= b.
            // Here [startPeriod, endPeriod] is the VAD-detected speech span
            // and [startTimeInMilliseconds, endTimeInMilliseconds] is the
            // query range.
            if(startPeriod <= endTimeInMilliseconds && startTimeInMilliseconds <= endPeriod){
                return true;
            }
        }
        return false;

    },



    detect : async function (audioBuffer){
        const monoFloat32Array = AudioBufferUtils.getInstance().mixDownToMono(audioBuffer);
        const voiceActivityDetector = await this.getVoiceActivityDetector();
        const results =  [];
        for await (const {audio, start, end} of voiceActivityDetector.run(monoFloat32Array, audioBuffer.sampleRate)) {
          results.push([start, end]);
        }
        return results
    },
    
    /**
     * Detect voice activity with specific configuration
     * @param {AudioBuffer} audioBuffer - The audio to analyze
     * @param {string} configType - Configuration type (SILENCE_DETECTION, WORD_BOUNDARY, BALANCED)
     * @returns {Promise<Array>} VAD results
     */
    detectWithConfig : async function (audioBuffer, configType = 'BALANCED'){
        try {
            console.log(`[VAD] Running detection with config: ${configType}`);
            const monoFloat32Array = AudioBufferUtils.getInstance().mixDownToMono(audioBuffer);
            const voiceActivityDetector = await this.getVoiceActivityDetectorWithConfig(configType);
            
            const results =  [];
            let segmentCount = 0;
            
            for await (const {audio, start, end} of voiceActivityDetector.run(monoFloat32Array, audioBuffer.sampleRate)) {
                results.push([start, end]);
                segmentCount++;
            }
            
            console.log(`[VAD] Detection completed. Found ${segmentCount} speech segments`);
            return results;
        } catch (error) {
            console.error(`[VAD] Detection failed with config ${configType}:`, error);
            throw new Error(`VAD detection failed: ${error.message}`);
        }
    },
    
    /**
     * Detect silences using aggressive configuration optimized for pure silence detection
     */
    detectSilences : async function (audioBuffer){
        return await this.detectWithConfig(audioBuffer, 'SILENCE_DETECTION');
    },
    
    /**
     * Detect voice activity around words using sensitive configuration
     */
    detectAroundWords : async function (audioBuffer){
        return await this.detectWithConfig(audioBuffer, 'WORD_BOUNDARY');
    },
    
    /**
     * Reset initialization failures and allow retry
     */
    resetInitializationFailures : function() {
        console.log('[VAD] Resetting initialization failures');
        this._initializationFailures = {};
        // Also clear any cached instances that might be in a bad state
        this._voiceActivityDetectorInstances = {};
        this._voiceActivityDetectorInstancesBeingCreated = {};
        this._voiceActivityDetectorInstance = null;
        this._voiceActivityDetectorInstanceBeingCreated = null;
    },
    
    /**
     * Get current initialization status
     */
    getInitializationStatus : function() {
        return {
            failures: this._initializationFailures,
            hasFailures: Object.keys(this._initializationFailures).length > 0,
            activeInstances: Object.keys(this._voiceActivityDetectorInstances)
        };
    },

    /**
     * Enhanced VAD analysis specifically for finding safe cut points.
     * Combines multiple detection strategies for better accuracy.
     * 
     * @param {AudioBuffer} audioBuffer - The audio to analyze
     * @param {Object} options - Analysis options
     * @returns {Object} Enhanced VAD results with cut point recommendations
     */
    detectWithCutPointAnalysis : async function(audioBuffer, options = {}) {
        const {
            minSilenceForCut = 80,      // Minimum silence duration for safe cut (ms)
            confidenceThreshold = 0.7,   // Confidence threshold for cut recommendations
            analyzeEnergyLevels = true,  // Include energy-based analysis
            smoothingWindow = 50         // Smoothing window for noise reduction (ms)
        } = options;

        // Get standard Silero VAD results using balanced config for cut point analysis
        const vadResults = await this.detectWithConfig(audioBuffer, 'BALANCED');
        
        // Enhanced analysis for cut points
        const enhancedResults = {
            voiceSegments: vadResults,
            silenceSegments: this.calculateSilenceSegments(vadResults, audioBuffer.duration),
            safeCutPoints: [],
            recommendations: []
        };

        // Find safe cut points in silence segments
        enhancedResults.silenceSegments.forEach(silence => {
            const duration = (silence.end - silence.start) * 1000; // Convert to ms
            
            if (duration >= minSilenceForCut) {
                const confidence = Math.min(duration / 200, 1.0); // Higher confidence for longer silences
                
                if (confidence >= confidenceThreshold) {
                    // Enhanced breathing pause detection
                    let reason = 'natural_pause';
                    let breathingConfidence = 0;
                    
                    if (duration > 300) {
                        // Likely breathing pause - analyze further
                        if (duration > 800) {
                            reason = 'long_breathing_pause';
                            breathingConfidence = 0.95;
                        } else if (duration > 500) {
                            reason = 'breathing_pause';
                            breathingConfidence = 0.85;
                        } else {
                            reason = 'short_breathing_pause';
                            breathingConfidence = 0.75;
                        }
                    } else if (duration > 150) {
                        // Could be end-of-sentence pause or quick breath
                        reason = 'speech_pause';
                        breathingConfidence = 0.6;
                    }
                    
                    enhancedResults.safeCutPoints.push({
                        timestamp: silence.start + (silence.end - silence.start) / 2, // Middle of silence
                        confidence: confidence,
                        breathingConfidence: breathingConfidence,
                        silenceDuration: duration,
                        reason: reason,
                        isBreathingPause: breathingConfidence > 0.7,
                        cutQuality: duration > 500 ? 'excellent' : duration > 300 ? 'good' : 'acceptable'
                    });
                }
            }
        });

        // Add energy-based analysis if requested
        if (analyzeEnergyLevels) {
            enhancedResults.energyAnalysis = this.analyzeEnergyLevels(audioBuffer, smoothingWindow);
        }

        // Generate cut recommendations
        enhancedResults.recommendations = this.generateCutRecommendations(enhancedResults);

        return enhancedResults;
    },

    /**
     * Calculate silence segments from VAD results
     */
    calculateSilenceSegments : function(vadResults, totalDuration) {
        const silenceSegments = [];
        let currentTime = 0;

        vadResults.forEach(speechSegment => {
            // Add silence before this speech segment
            if (currentTime < speechSegment[0]) {
                silenceSegments.push({
                    start: currentTime,
                    end: speechSegment[0]
                });
            }
            currentTime = speechSegment[1];
        });

        // Add final silence if any
        if (currentTime < totalDuration) {
            silenceSegments.push({
                start: currentTime,
                end: totalDuration
            });
        }

        return silenceSegments;
    },

    /**
     * Analyze energy levels for additional cut point detection
     */
    analyzeEnergyLevels : function(audioBuffer, windowMs) {
        // This would implement RMS energy analysis
        // to find low-energy regions that might be good cut points
        // even if VAD doesn't detect them as pure silence
        
        const windowSamples = Math.floor(audioBuffer.sampleRate * windowMs / 1000);
        const energyLevels = [];
        
        // Simplified energy calculation
        const channelData = audioBuffer.getChannelData(0);
        for (let i = 0; i < channelData.length; i += windowSamples) {
            let sum = 0;
            const end = Math.min(i + windowSamples, channelData.length);
            
            for (let j = i; j < end; j++) {
                sum += channelData[j] * channelData[j];
            }
            
            const rms = Math.sqrt(sum / (end - i));
            energyLevels.push({
                timestamp: i / audioBuffer.sampleRate,
                energy: rms
            });
        }
        
        return energyLevels;
    },

    /**
     * Generate cut recommendations based on all available analysis
     */
    generateCutRecommendations : function(analysisResults) {
        const recommendations = [];
        
        // Add high-confidence cut points
        analysisResults.safeCutPoints.forEach(cutPoint => {
            if (cutPoint.confidence > 0.8) {
                recommendations.push({
                    type: 'high_confidence_cut',
                    timestamp: cutPoint.timestamp,
                    reason: `${cutPoint.reason} (${Math.round(cutPoint.silenceDuration)}ms silence)`,
                    confidence: cutPoint.confidence
                });
            }
        });

        return recommendations.sort((a, b) => b.confidence - a.confidence);
    },







});



const VoiceActivityDetectionHelperInstance = new VoiceActivityDetectionHelper();

export default {
    getInstance: function ()
    {
        return VoiceActivityDetectionHelperInstance;
    }
}; 